import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tabs, Input, Button, Modal, Checkbox, Empty, Spin, Tooltip, Typography, message } from "antd";
import {
  SearchOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  VideoCameraOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import { getPatientAppointmentList, cancelPatientZoomAppointment } from "../../redux/patientSlice";
import { baseUrl } from "../../../utils/envAccess";
import { mergeAppointments, splitUpcomingPast, SOURCE_ZOOM } from "../../../utils/normalizeAppointments";
import neutralDoctorAvatar from "../../../assets/images/users/clinician_default_avatar.png";
import "./PatientAppointments.css";

const { Text, Title } = Typography;
const { TextArea } = Input;

function normalizeName(value) {
  return String(value || "").trim().toLowerCase();
}

const CANCEL_CUTOFF_HOURS = 48;

function isPastCancelCutoff(appt) {
  if (!appt?.startMoment) return false;
  return appt.startMoment.diff(moment(), "hours") < CANCEL_CUTOFF_HOURS;
}


function manageUrlFor(appt) {
  return appt?.raw?.manageUrl ?? appt?.raw?.ManageUrl ?? null;
}

const VIRTUAL_CARE_TERMS = [
  "I provide my informed consent to receive virtual care via videocall.",
  "I confirm I am in a private and appropriate setting for discussing my medication and health history.",
  "I understand that if we are disconnected, or I need to follow up, the care provider's contact information can be found in Appointments.",
  "I understand that the date and time of this virtual consultation will be documented in my medical record.",
];

/**
 * Virtual Care Consent & Confirmation gate.
 */
export function VirtualCareConsentModal({ open, onCancel, onConfirm }) {
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (open) setChecked(false);
  }, [open]);

  return (
    <Modal
      title="Virtual Care Consent & Confirmation"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={640}
      destroyOnClose
    >
      <Text style={{ display: "block", color: "#595959", marginBottom: 16 }}>
        By checking the box below, I confirm that I understand and agree to the
        following before beginning my virtual care appointment:
      </Text>
      <ul className="pa-consent-list">
        {VIRTUAL_CARE_TERMS.map((term) => (
          <li key={term}>{term}</li>
        ))}
      </ul>
      <Checkbox
        checked={checked}
        onChange={(e) => setChecked(e.target.checked)}
        style={{ marginTop: 12 }}
      >
        I confirm and consent to proceed with virtual care
      </Checkbox>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
        <Button
          type="primary"
          disabled={!checked}
          icon={<VideoCameraOutlined />}
          style={{ background: checked ? "#00ADEF" : undefined, borderColor: checked ? "#00ADEF" : undefined }}
          onClick={onConfirm}
        >
          Join Call
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Cancel confirmation gate - collects an optional reason before calling
 * Patient/CancelAppointment.
 */
function CancelAppointmentModal({ open, appt, submitting, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  return (
    <Modal
      title="Cancel Appointment"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={520}
      destroyOnClose
    >
      <Text style={{ display: "block", color: "#595959", marginBottom: 12 }}>
        {appt
          ? `Cancel your appointment with ${appt.providerName || "your care provider"} on ${appt.startMoment.format(
              "MMMM D, YYYY"
            )} at ${appt.startMoment.format("h:mmA")}?`
          : "Cancel this appointment?"}
      </Text>
      <TextArea
        rows={3}
        placeholder="Reason for cancelling (optional)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={500}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
        <Button onClick={onCancel} disabled={submitting}>
          Keep appointment
        </Button>
        <Button danger type="primary" loading={submitting} onClick={() => onConfirm(reason)}>
          Cancel appointment
        </Button>
      </div>
    </Modal>
  );
}

export default function PatientAppointments() {
  const dispatch = useDispatch();

  const zoomAppointments = useSelector((state) => state?.patient?.zoomAppointments);

  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [photoMap, setPhotoMap] = useState(() => new Map());
  const [consentOpen, setConsentOpen] = useState(false);
  const [pendingMeetingLink, setPendingMeetingLink] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // The legacy AppointmentMaster read path (Patient/GetUpComingAppointment) was
  // removed from the backend; only the Zoom-based store is fetched now.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    dispatch(getPatientAppointmentList()).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  
  useEffect(() => {
    let cancelled = false;

    const fetchProviderPhotos = async () => {
      try {
        const date = moment().add(7, "days").format("YYYY-MM-DD");
        const res = await axios.get(`${baseUrl}Patient/GetAllDoctorsAllTimeSlots`, {
          params: { date },
          headers: { accept: "text/plain" },
        });

        const raw = res?.data?.data ?? res?.data;
        const items = Array.isArray(raw) ? raw : raw?.items || raw?.doctors || raw?.results || [];
        const list = Array.isArray(items) ? items : [];

        const map = new Map();
        list.forEach((d) => {
          const id = d?.userId ?? d?.UserId ?? d?.doctorId ?? d?.DoctorId ?? d?.id ?? d?.Id;
          const name = d?.doctorName ?? d?.DoctorName ?? d?.name ?? d?.Name;
          const photo =
            d?.picture ||
            d?.Picture ||
            d?.photo ||
            d?.Photo ||
            d?.profile ||
            d?.Profile ||
            d?.avatar ||
            d?.Avatar;
          if (!photo) return;
          if (id != null) map.set(Number(id), photo);
          if (name) map.set(normalizeName(name), photo);
        });

        if (!cancelled) setPhotoMap(map);
      } catch (e) {
        if (!cancelled) setPhotoMap(new Map());
      }
    };

    fetchProviderPhotos();
    return () => {
      cancelled = true;
    };
  }, []);

  const photoFor = useCallback(
    (appt) => photoMap.get(normalizeName(appt?.providerName)) || neutralDoctorAvatar,
    [photoMap]
  );

  const allAppointments = useMemo(
    () =>
      mergeAppointments({
        zoom: zoomAppointments,
        viewer: "patient",
      }),
    [zoomAppointments]
  );

  const filteredBySearch = useMemo(() => {
    if (!searchParam) return allAppointments;
    const q = searchParam.toLowerCase();
    return allAppointments.filter((a) => normalizeName(a?.providerName).includes(q));
  }, [allAppointments, searchParam]);

  const { upcoming, past } = useMemo(
    () => splitUpcomingPast(filteredBySearch),
    [filteredBySearch]
  );

  const handleJoin = useCallback((meetingLink) => {
    setPendingMeetingLink(meetingLink);
    setConsentOpen(true);
  }, []);

  const handleConfirmConsent = useCallback(() => {
    if (pendingMeetingLink) window.open(pendingMeetingLink, "_blank");
    setConsentOpen(false);
    setPendingMeetingLink(null);
  }, [pendingMeetingLink]);

  const handleConfirmCancel = useCallback(
    (reason) => {
      if (!cancelTarget?.id) return;
      setCancelling(true);
      dispatch(cancelPatientZoomAppointment({ appointmentId: cancelTarget.id, reason }))
        .then((response) => {
          if (cancelPatientZoomAppointment.fulfilled.match(response)) {
            message.success("Appointment cancelled.");
            setCancelTarget(null);
          } else {
            message.error(
              response?.payload?.message || response?.payload?.Message || "Failed to cancel appointment."
            );
          }
        })
        .finally(() => setCancelling(false));
    },
    [cancelTarget, dispatch]
  );

  const renderCard = (appt, isPast) => {
    const dateLabel = appt.startMoment.format("MMMM D, YYYY");
    const timeLabel = appt.endMoment
      ? `${appt.startMoment.format("h:mmA")} - ${appt.endMoment.format("h:mmA")}`
      : appt.startMoment.format("h:mmA");

    return (
      <div
        className="pa-card"
        key={`${appt.source}-${appt.id ?? ""}-${appt.startMoment.valueOf()}`}
      >
        <img
          className="pa-card-avatar"
          src={photoFor(appt)}
          alt=""
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = neutralDoctorAvatar;
          }}
        />

        <div className="pa-card-provider">
          <h3 className="pa-card-name">{appt.providerName || "Care provider"}</h3>
          {!!appt.roleName && <p className="pa-card-role">{appt.roleName}</p>}
        </div>

        <div className="pa-card-meta">
          <div className="pa-card-meta-row">
            <CalendarOutlined className="pa-card-meta-icon" aria-hidden />
            <span>{dateLabel}</span>
          </div>
          {!!timeLabel && (
            <div className="pa-card-meta-row">
              <ClockCircleOutlined className="pa-card-meta-icon" aria-hidden />
              <span>{timeLabel}</span>
            </div>
          )}
          <div className="pa-card-meta-row">
            <EnvironmentOutlined className="pa-card-meta-icon" aria-hidden />
            <span>{appt.locationLabel}</span>
          </div>
        </div>

        <div className="pa-card-action" style={{ display: "flex", gap: 8 }}>
          {!isPast && (
            <Tooltip
              title={
                appt.meetingLink
                  ? ""
                  : "Join link not available yet. Please contact your care team."
              }
            >
              <Button
                type="primary"
                icon={<VideoCameraOutlined />}
                disabled={!appt.meetingLink}
                style={
                  appt.meetingLink
                    ? { background: "#00ADEF", borderColor: "#00ADEF" }
                    : undefined
                }
                onClick={() => handleJoin(appt.meetingLink)}
              >
                JOIN
              </Button>
            </Tooltip>
          )}
          {/* Cancellation only works for Zoom-sourced bookings - CancelAppointment
              looks the id up in the Appointment table, which legacy records aren't in. */}
          {!isPast && appt.source === SOURCE_ZOOM && !!appt.id && (() => {
            const manageUrl = manageUrlFor(appt);
            const pastCutoff = isPastCancelCutoff(appt);
            return (
              <Tooltip
                title={
                  pastCutoff
                    ? "This appointment is within 48 hours of its start time and can no longer be cancelled online. Please contact support to cancel."
                    : ""
                }
              >
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  disabled={pastCutoff}
                  onClick={() =>
                    manageUrl
                      ? window.open(manageUrl, "_blank", "noopener,noreferrer")
                      : setCancelTarget(appt)
                  }
                >
                  Cancel
                </Button>
              </Tooltip>
            );
          })()}
        </div>
      </div>
    );
  };

  const renderList = (items, isPast) => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin />
        </div>
      );
    }
    if (!items.length) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={isPast ? "No past appointments" : "No upcoming appointments"}
          style={{ padding: 48 }}
        />
      );
    }
    return <div className="pa-card-list">{items.map((a) => renderCard(a, isPast))}</div>;
  };

  const tabItems = [
    {
      key: "upcoming",
      label: `Upcoming (${upcoming.length})`,
      children: renderList(upcoming, false),
    },
    {
      key: "past",
      label: `Past (${past.length})`,
      children: renderList(past, true),
    },
  ];

  return (
    <div className="pa-wrapper">
      <Title level={3} className="pa-heading">
        Appointments
      </Title>

      <Tabs
        items={tabItems}
        tabBarExtraContent={
          <Input
            placeholder="Search"
            value={searchParam}
            prefix={<SearchOutlined />}
            onChange={(e) => setSearchParam(e.target.value)}
            style={{ width: 220 }}
            allowClear
          />
        }
      />

      <VirtualCareConsentModal
        open={consentOpen}
        onCancel={() => {
          setConsentOpen(false);
          setPendingMeetingLink(null);
        }}
        onConfirm={handleConfirmConsent}
      />

      <CancelAppointmentModal
        open={!!cancelTarget}
        appt={cancelTarget}
        submitting={cancelling}
        onCancel={() => setCancelTarget(null)}
        onConfirm={handleConfirmCancel}
      />
    </div>
  );
}
